<!-- src/views/consentimientos/ConsentimientoModal.vue -->
<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="cerrar">
      <div class="modal">

        <!-- Header -->
        <div class="modal-header">
          <div class="modal-titulo">
            <span class="modal-icono">📋</span>
            <div>
              <h2>Consentimiento Informado</h2>
              <p class="modal-subtitulo" v-if="consentimientoActual">
                {{ consentimientoActual.nombrePlantilla }}
              </p>
            </div>
          </div>
          <button class="btn-cerrar" @click="cerrar">✕</button>
        </div>

        <!-- Stepper -->
        <div class="stepper">
          <div
            v-for="(paso, i) in pasos"
            :key="i"
            class="paso"
            :class="{ activo: pasoActual === i, completado: pasoActual > i }"
          >
            <div class="paso-circulo">
              <span v-if="pasoActual > i">✓</span>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="paso-label">{{ paso }}</span>
          </div>
        </div>

        <!-- Contenido -->
        <div class="modal-body">

          <!-- Paso 0: Generar -->
          <div v-if="pasoActual === 0" class="paso-contenido">
            <div class="info-box">
              <p>Se generará el consentimiento informado para la atención
                <strong>#{{ idAtencion }}</strong>.
              </p>
            </div>
            <div class="campo">
              <label>Tipo de consentimiento</label>
              <select v-model="codigoPlantilla">
                <option value="">-- Seleccione --</option>
                <option value="DATOS_PERSONALES">Tratamiento de datos personales</option>
                <option value="CIRUGIA_GENERAL">Cirugía general</option>
                <option value="PROCEDIMIENTO_INVASIVO">Procedimiento invasivo</option>
                <option value="ANESTESIA">Anestesia</option>
              </select>
            </div>
            <div v-if="error" class="alerta-error">{{ error }}</div>
          </div>

          <!-- Paso 1: Leer -->
          <div v-if="pasoActual === 1" class="paso-contenido">
            <div
              class="documento-preview"
              v-html="consentimientoActual && consentimientoActual.htmlRenderizado"
              @scroll="verificarLectura"
            />
            <label class="check-lectura">
              <input type="checkbox" v-model="haLeido" />
              <span>He leído y comprendo el contenido de este documento</span>
            </label>
            <div v-if="!scrollCompleto" class="aviso-scroll">
              ↓ Desplácese hasta el final para continuar
            </div>
          </div>

          <!-- Paso 2: Firmar -->
          <div v-if="pasoActual === 2" class="paso-contenido">
            <div class="campo">
              <label>Nombre completo del firmante</label>
              <input
                v-model="firmadoPor"
                type="text"
                placeholder="Nombre del paciente o representante legal"
              />
            </div>
            <signature-pad
              :ancho="500"
              :alto="160"
              @update:firma="onFirmaCapturada"
            />
            <div v-if="error" class="alerta-error">{{ error }}</div>
          </div>

          <!-- Paso 3: Completado -->
          <div v-if="pasoActual === 3" class="paso-contenido paso-exito">
            <div class="exito-icono">✓</div>
            <h3>Consentimiento firmado exitosamente</h3>
            <p>El documento ha sido registrado y el PDF generado.</p>
            <button
              class="btn-pdf"
              @click="descargarPdf(consentimientoActual.idConsentimiento)"
            >
              ⬇ Descargar PDF
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button
            v-if="pasoActual > 0 && pasoActual < 3"
            class="btn-secundario"
            :disabled="cargando"
            @click="pasoActual--"
          >
            ← Atrás
          </button>

          <button
            v-if="pasoActual === 0"
            class="btn-secundario"
            @click="cerrar"
          >
            Cancelar
          </button>

          <button
            v-if="pasoActual === 0"
            class="btn-primario"
            :disabled="!codigoPlantilla || cargando"
            @click="generarConsentimiento"
          >
            {{ cargando ? 'Generando...' : 'Generar documento →' }}
          </button>

          <button
            v-if="pasoActual === 1"
            class="btn-primario"
            :disabled="!haLeido"
            @click="pasoActual = 2"
          >
            Continuar a firma →
          </button>

          <button
            v-if="pasoActual === 2"
            class="btn-primario"
            :disabled="!firmaBase64 || !firmadoPor || cargando"
            @click="registrarFirma"
          >
            {{ cargando ? 'Registrando...' : '✓ Firmar y guardar' }}
          </button>

          <button
            v-if="pasoActual === 3"
            class="btn-primario"
            @click="cerrar"
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script>
import SignaturePad from './SignaturePad.vue'
import { consentimientoApi } from './consentimientoApi'

export default {
  name: 'ConsentimientoModal',

  components: { SignaturePad },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    idAtencion: {
      type: Number,
      required: true
    }
  },

  emits: ['update:visible', 'firmado'],

  data() {
    return {
      pasos: ['Generar', 'Leer', 'Firmar', 'Completado'],
      pasoActual: 0,
      codigoPlantilla: '',
      haLeido: false,
      scrollCompleto: false,
      firmaBase64: null,
      firmadoPor: '',
      consentimientoActual: null,
      cargando: false,
      error: null
    }
  },

  watch: {
    visible(val) {
      if (val) this.resetear()
    }
  },

  methods: {
    resetear() {
      this.pasoActual = 0
      this.codigoPlantilla = ''
      this.haLeido = false
      this.scrollCompleto = false
      this.firmaBase64 = null
      this.firmadoPor = ''
      this.consentimientoActual = null
      this.cargando = false
      this.error = null
    },

    cerrar() {
      this.$emit('update:visible', false)
    },

    onFirmaCapturada(base64) {
      this.firmaBase64 = base64
    },

    verificarLectura(e) {
      const el = e.target
      const alFinal = el.scrollHeight - el.scrollTop <= el.clientHeight + 40
      if (alFinal) this.scrollCompleto = true
    },

    async generarConsentimiento() {
      this.cargando = true
      this.error = null
      try {
        const { data } = await consentimientoApi.crear(
          this.idAtencion,
          this.codigoPlantilla
        )
        this.consentimientoActual = data
        this.pasoActual = 1
      } catch (e) {
        this.error = 'Error al generar el consentimiento. Intente nuevamente.'
      } finally {
        this.cargando = false
      }
    },

    async registrarFirma() {
      this.cargando = true
      this.error = null
      try {
        const payload = {
          firmaBase64: this.firmaBase64,
          firmadoPor: this.firmadoPor,
          direccionIp: await this.obtenerIp(),
          userAgent: navigator.userAgent
        }
        const { data } = await consentimientoApi.firmar(
          this.consentimientoActual.idConsentimiento,
          payload
        )
        this.consentimientoActual = data
        this.pasoActual = 3
        this.$emit('firmado', data)
      } catch (e) {
        this.error = 'Error al registrar la firma. Intente nuevamente.'
      } finally {
        this.cargando = false
      }
    },

    async descargarPdf(idConsentimiento) {
      try {
        const res = await consentimientoApi.descargarPdf(idConsentimiento)
        const url = URL.createObjectURL(res.data)
        const a = document.createElement('a')
        a.href = url
        a.download = 'consentimiento_' + idConsentimiento + '.pdf'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        this.error = 'Error al descargar el PDF.'
      }
    },

    async obtenerIp() {
      try {
        const res = await fetch('https://api.ipify.org?format=json')
        const data = await res.json()
        return data.ip
      } catch {
        return 'desconocida'
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
  overflow-y: auto;
}

.modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
}
.modal-titulo {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.modal-icono { font-size: 28px; }
.modal-titulo h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}
.modal-subtitulo {
  margin: 2px 0 0;
  font-size: 13px;
  color: #6b7280;
}
.btn-cerrar {
  background: none;
  border: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}
.btn-cerrar:hover { background: #f3f4f6; color: #374151; }

/* Stepper */
.stepper {
  display: flex;
  padding: 20px 24px;
  gap: 0;
  border-bottom: 1px solid #f3f4f6;
}
.paso {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
}
.paso:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 28px;
  right: 0;
  top: 14px;
  height: 2px;
  background: #e5e7eb;
  z-index: 0;
}
.paso.completado:not(:last-child)::after { background: #6366f1; }
.paso-circulo {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #9ca3af;
  background: #fff;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.2s;
}
.paso.activo .paso-circulo {
  border-color: #6366f1;
  color: #6366f1;
  background: #eef2ff;
}
.paso.completado .paso-circulo {
  border-color: #6366f1;
  background: #6366f1;
  color: #fff;
}
.paso-label {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
}
.paso.activo .paso-label   { color: #6366f1; font-weight: 600; }
.paso.completado .paso-label { color: #6366f1; }

/* Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
.paso-contenido {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-box {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 14px;
  color: #3730a3;
}

.campo label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.campo select,
.campo input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.campo select:focus,
.campo input:focus { border-color: #6366f1; }

.documento-preview {
  height: 280px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  font-size: 13px;
  line-height: 1.7;
  color: #374151;
  background: #fafafa;
}

.check-lectura {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}
.check-lectura input { margin-top: 2px; accent-color: #6366f1; }

.aviso-scroll {
  font-size: 12px;
  color: #f59e0b;
  text-align: center;
}

.alerta-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #dc2626;
}

/* Paso éxito */
.paso-exito {
  align-items: center;
  text-align: center;
  padding: 20px 0;
}
.exito-icono {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #d1fae5;
  color: #059669;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.paso-exito h3 { margin: 0; font-size: 18px; color: #111827; }
.paso-exito p  { margin: 0; font-size: 14px; color: #6b7280; }
.btn-pdf {
  padding: 10px 24px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  color: #4338ca;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-pdf:hover { background: #e0e7ff; }

/* Footer */
.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.btn-primario {
  padding: 10px 24px;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-primario:hover:not(:disabled) { background: #4f46e5; }
.btn-primario:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secundario {
  padding: 10px 20px;
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-secundario:hover:not(:disabled) { background: #f9fafb; }
.btn-secundario:disabled { opacity: 0.5; cursor: not-allowed; }
</style>